import {
    CountMetric,
    LatencyMetric,
    EnvironmentDetail,
    globalEventBus,
    UnsupportedEnvironmentException,
    VoiceNotEnabledException,
    InvalidInputException,
    WakeWordTimeoutException,
    UnsupportedBrowserException,
    StopIntentException,
    AlternativeBotException,
    PromiseUtils,
    PauseIntentException,
    RepeatIntentException,
    ConfirmationDeclinedException,
    InvalidSpeechException,
} from '@amzn/ihm-associate-tools-js-lib'
import { SeawardEventBusTopicsConstants } from '@utils/Constants/SeawardEventBusTopicsConstants'
import * as CandyCaneService from '@services/CandyCaneService'
import { CandyCaneWakeWordErrorMappings } from '@services/voice/CandyCaneWakeWordErrorMappings'
import * as VoiceUtilsService from '@utils/VoiceUtilsService'
import _ from 'lodash'
import log from 'console'

import * as speechSynthesisService from '@services/speech/SpeechSynthesisService'
import { wakeWordListener } from '@crossframe/listeners/wakeWordListener'
import { SpeechRecognitionService } from '@services/voice/SpeechRecognitionService'
import { LogProvider } from '@amzn/ihm-associate-tools-meridian-components/dist/ts-build/LDFramework/Log'

const programName = 'peppermintServiceCore'
let initializePromise

const PeppermintServiceConstants = Object.freeze({
    CONFIRMATION_PROMPT_FORMAT: 'You said {0}, right?',
    PAUSE_INTENT_PROMPT: 'Sure, I will pause. Say the wake word when you are ready to answer',
    DEFAULT_RETRY_PROMPT: 'Sorry, can you please repeat that?',
    DEFAULT_CONFIRMATION_RETRIES: 2,
    CONFIRMATION_RESULT_YES: 'Yes',
    CONFIRMATION_RESULT_NO: 'No',
})

const NON_FAULT_EXCEPTION_WHITELIST = Object.freeze([
    InvalidInputException,
    UnsupportedBrowserException,
    VoiceNotEnabledException,
    StopIntentException,
    AlternativeBotException,
])

/**
 * Warms up all of the dependencies to reduce latency.
 *
 * @return {Promise} Resolves when warmup is done successfully. Rejects otherwise. Note that the warmup method is there to reduce initial
 *   latency and failures may be transient.
 */
export const warmup = () => {
    const warmupFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'peppermintService.warmup')
    const warmupLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'peppermintService.warmup')
    warmupLatencyMetrics.start()

    return initialize()
        .then(() => {
            warmupLatencyMetrics.stop()
            warmupFaultMetrics.count(0)
        })
        .catch((err) => {
            console.warn('Warmup failed with error', err)
            return handleError(err, warmupFaultMetrics)

        })
        .finally(() => {
            warmupLatencyMetrics.stop()
        })
}

/*
 * All requests go through this function. It expects a peppermintRequestConfig
 * {
 *     requestConfig: {object} config coming from the caller.
 *     speechRecognitionFn: {function} the function to call to make the speech recognition request.
 *     convertResultToSpeechSynthesisFn: {function} function that converts text to a format that makes sense for speech synthesis.
 *     speechRecognitionFunctionName: {string} the function that will be called.
 *     retryPrompt: {string} prompt to ask the user if we couldn't recognize their speech.
 *     statsMetricName: {string} optional metric name for the statistics that will be emitted from the request (defaults to speechRecognitionFunctionName).
 * }
 */
export const handlePeppermintRequest = (peppermintRequestConfig) => {
    const faultMetric = new CountMetric(undefined, 'Fault', programName, 'handlePeppermintRequest')
    const latencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'handlePeppermintRequest')
    latencyMetrics.start()

    return initialize()
        .then(() => {
            return validateConfig(peppermintRequestConfig.requestConfig)
        })
        .then(() => {
            return buildStateObject(peppermintRequestConfig)
        })
        .then(startRequestForState)
        .then((data) => {
            faultMetric.count(0)
            return data
        })
        .catch((e) => {
            return handleError(e, faultMetric)
        })
}

/**
 * Builds a state object that we use to track the state of the current request.
 * @param {object} peppermintRequestConfig - The configuration object that includes the caller configuration and the specific configuration for
 *  the function.
 * @return {object} An object containing the state that we use for the duration of the request. This state includes statistics that we use to track
 *  what happens in each request and we emit metrics for it.
 * {
 *       prompt: {string} Prompt asked to the user.  Example, "What is the expiration date?"
 *       promptOverride: {string}  Overrides the prompt for just one time (it's cleared after it is used).
 *       retries: {number} Number of retries before we give up because we couldn't understand what the user was saying.
 *       confirmResponse: {boolean}  Confirm with the user that we understood what they said. Example: "You said 10, right?".
 *       confirmationRetries: {number} Number of retries to get confirmation before we give up.
 *       waitForWakeWord: {boolean} Whether to wait for wakeWord before running speech recognition or not (this flag is cleared after it's used).
 *       speechRecognitionConfig: {object} Config specific for the {@see speechRecognitionService}.
 *       wakeWordConfig: {object} Config specific for the {@see wakeWordService}.
 *       speechRecognitionFn: {function} Function to call from the {@see speechRecognitionService}.
 *       getConfirmationFn: {function}  {@see peppermintService} function to call to get a confirmation.
 *       convertResultToSpeechSynthesisFn: {function} Function to convert the result from {@see speechRecognitionService} to something readable by {@see speechSynthesisService}.
 *       convertAlternativeBotResultToSpeechSynthesisFn: {function} Function to convert alternative bot result from {@see speechRecognitionService} to something readable by {@see speechSynthesisService}.
 *       retryPrompt: {string} Prompt to ask the user to retry (can be specific per data type).
 *       speechRecognitionFunctionName: {string} Name of the function to call from the {@see speechRecognitionService}.
 *       statsMetricName: {string} Metric name to append to the stats metrics that we emit.
 *       confirmedRecognition: {string} Gets filled with the confirmed data returned from {@see speechRecognitionService} (only if it was state.confirmResponse is true and the user confirmed).
 *       confirmDeclinedRecognitions: {array{string}} Holds all data returned from {@see speechRecognitionService} that was declined by the user.
 *       stats: {} {object} Holds statistic counters for different events which we use to send metrics at the end of the request.
 * }
 */
export const buildStateObject = (peppermintRequestConfig) => {
    const requestConfig = peppermintRequestConfig.requestConfig
    // We explicitly copy everything instead of doing object.extend to control our inputs (we never know what the user will send)
    // and to make sure that we don't overwrite something in the future. Seeing the keys that are set here makes it easier to spot
    // if we're overriding a key coming from the config. The tradeoff is having to "whitelist" each new key here.

    return {
        prompt: requestConfig.prompt,
        promptOverride: null,
        retries: requestConfig.retries,
        confirmResponse: requestConfig.confirmResponse,
        confirmationRetries: requestConfig.confirmationRetries || PeppermintServiceConstants.DEFAULT_CONFIRMATION_RETRIES,
        waitForWakeWord: requestConfig.waitForWakeWord,
        speechRecognitionConfig: _.cloneDeep(requestConfig.speechRecognitionConfig),
        wakeWordConfig: _.cloneDeep(requestConfig.wakeWordConfig),
        speechRecognitionFn: peppermintRequestConfig.speechRecognitionFn,
        getConfirmationFn: peppermintRequestConfig.getConfirmationFn,
        convertResultToSpeechSynthesisFn: peppermintRequestConfig.convertResultToSpeechSynthesisFn,
        convertAlternativeBotResultToSpeechSynthesisFn: peppermintRequestConfig.convertAlternativeBotResultToSpeechSynthesisFn,
        retryPrompt: peppermintRequestConfig.retryPrompt,
        speechRecognitionFunctionName: peppermintRequestConfig.speechRecognitionFunctionName,
        statsMetricName: peppermintRequestConfig.statsMetricName || peppermintRequestConfig.speechRecognitionFunctionName,
        confirmedRecognition: null,
        confirmDeclinedRecognitions: [],
        stats: {},
    }
}

/**
 * Starts the request workflow and emits metrics at the end.
 * @param {object} state {@see buildStateObject}
 * @return {Promise} resolves/rejects with the data coming from {@see speechRecognitionService} or if retries are exhausted.
 */
export const startRequestForState = (state) => {
    return askAndListen(state)
    .catch((error) => {
        if (error instanceof InvalidSpeechException) {
            console.error('startRequestForState AlternativeBotException with error ', error)

            // We do this here because we only want to count this once when we reject. Since askAndListen is recursive,
            // if we reject from a child with this exception, and there's no more retries left, we would count each
            // recursion level for the same exception. Instead, we don't count if we have no more retries left and count
            // once in here.
            incrementStats(state, 'invalidSpeechCount')
            console.warn('Could not understand and there are no more retries left')
            return Promise.reject(error)
        } else if (error instanceof StopIntentException) {
            // We do this here because askAndListen is recursive and if a child rejects with StopIntentException
            // we could end up counting it twice. StopIntentException should only be counted once.
            incrementStats(state, 'stopIntentCount')
            console.debug('Got StopIntentException. Rejecting...', error)
            return Promise.reject(error)
        } else {
            console.warn('Failed', state.speechRecognitionFunctionName, 'with', error)
            return Promise.reject(error)
        }
    })
    .finally(() => {
        emitStatMetrics(state)
        console.debug('stats', state.stats)
    })
}

/**
 * Orchestrates the entire workflow to attempt to extract the desired data from speech input. Here's a rough overview:
 *  1. Asks the user (using {@see speechSynthesisService}) to say the input.
 *  2. Waits for the wake word (if configured to do so). Note that we only wait for the wake word once, unless the
 *      user asks to "pause".
 *  3. Records the user input and performs speech recognition on it. This call could fail if we can't recognize what the
 *      user is said or if they have a different intent (Pause/Stop/Repeat).
 *  4. If the {@see speechRecognitionService} resolves, we confirm the result by reading it back to the associate (if
 *      state.confirmResponse is true).
 *  5. Rejects with AlternativeBotException if a bot other than the target bot recognizes the speech input.
 *       NOTE: AlternativeBotException should always be the first exception in the chain of exceptions below to avoid
 *       repeated confirmations after the user confirms the response once.
 *  6. Repeats the prompt if the {@see speechRecognitionService} rejects with RepeatIntentException (the user said "repeat").
 *      This effectively starts over the workflow.
 *  7. Pauses (waits for the wakeword) if the {@see speechRecognitionService} rejects with PauseIntentException (the user
 *      said "pause").
 *  8. Retries up to state.retries to extract what the data from the speech input coming from the user and rejects if
 *      it can't understand and all retries are exhausted.
 *  9. Rejects with StopIntentException if, at any point, the user says "stop".
 * @param {object} state {@see buildStateObject}.
 * @return {Promise} resolves if we can extract the data or rejects with specific exceptions (see service's doc).
 */
export const askAndListen = (state) => {
    incrementStats(state, 'askAndListenCallCount')
    return ask(state)
    // We're about to start recording and we don't want the wake word listening at the same time.
    .then(cancelWakeWord)
    .then(() => {
        return waitForWakeWordIfNecessary(state)
    })
    .then(() => {
        incrementStats(state, 'recognizeSpeechCount')
        const response = state.speechRecognitionFn(state.speechRecognitionConfig)
        return response
    })
    .then((result) => {
        return confirmResponseIfNecessary(state, state.convertResultToSpeechSynthesisFn, result)
    })
    .catch((error) => {
        alert(error)
        console.error('askAndListen failed with error ', error)

        if (error instanceof AlternativeBotException) {
            console.error('askAndListen AlternativeBotException with error ', error)
            return confirmResponseIfNecessary(state, state.convertAlternativeBotResultToSpeechSynthesisFn, error.extraData)
            .then((response) => {
                incrementStats(state, 'alternativeBotCount')
                console.info('Got AlternativeBotException. data:', response.data, 'botName:', response.botName)
                return Promise.reject(error)

            })
        } else if (error instanceof PauseIntentException) {
            console.error('askAndListen PauseIntentException with error ', error)
            incrementStats(state, 'pauseIntentCount')
            state.promptOverride = PeppermintServiceConstants.PAUSE_INTENT_PROMPT
            state.waitForWakeWord = true
            return askAndListen(state)
        } else if (error instanceof RepeatIntentException) {
            console.error('askAndListen RepeatIntentException with error ', error)
            incrementStats(state, 'repeatIntentCount')
            return askAndListen(state)
        } else if (error instanceof ConfirmationDeclinedException) {
            console.error('askAndListen ConfirmationDeclinedException with error ', error)
            incrementStats(state, 'confirmResponseDeclinedCount')
            if (!state.retries) {
                return Promise.reject(new InvalidSpeechException({
                    message: 'Confirmation failed for speech',
                }))
            }
            state.retries--
            return askAndListen(state)
        } else if (error instanceof InvalidSpeechException) {
            console.error('askAndListen InvalidSpeechException with error ', error)
            if (!state.retries) {
                return Promise.reject(error)
            }

            incrementStats(state, 'invalidSpeechCount')
            state.promptOverride = state.retryPrompt
            state.retries--
            return askAndListen(state)
        } else {
            console.error('askAndListen Generic  with error ', error)
            if (!state.retries) {
                return Promise.reject(error)
            }

            incrementStats(state, 'invalidSpeechCount')
            state.promptOverride = state.retryPrompt
            state.retries--
            return askAndListen(state)

        }
    })
}

/**
 * Uses {@see speechSynthesisService} to prompt the user for something. It uses the promptOverride key, if set, and
 * falls back to the prompt key. Note that this unsets the promptOverride key so the caller will need to set it again
 * if they want to override again.
 *
 * @param {object} state {@see buildStateObject}.
 * @return {Promise} {@see speechSynthesisService.speak}.
 */
function ask (state) {
    const promptText = state.promptOverride || state.prompt
    state.promptOverride = null
    const response = speechSynthesisService.speak(promptText)
    return response
}

/**
 * Calls the wakeWordService to wait for the wake word if state.waitForWakeWord is true.
 * @param {object} state {@see buildStateObject}.
 * @return {Promise} resolves if we don't want to wait for the wake word or whenever the user says the wake word ("Alexa").
 *  May reject if another caller calls to stop the wake word while we're waiting for it.
 */
export const waitForWakeWordIfNecessary = (state) => {
    if (!state.waitForWakeWord) {
        return Promise.resolve()
    }
    incrementStats(state, 'waitForWakeWordCount')
    // We should only need the wake word once, unless the user says "pause"
    state.waitForWakeWord = false
    const messsage = {
        data: {
            action: 'listen',
            parameters: null,
        },
    }
    return wakeWordListener(messsage)
}

/**
 * Asks the user to confirm what they just said (if state.confirmResponse is true). Returns a promise that resolves
 * with the value (if the user confirms it's correct) or rejects if the user confirms that the response is incorrect.
 * @param {object} state {@see buildStateObject}.
 * @param {function} speechSynthesisFn Function to convert the result from {@see speechRecognitionService} to something readable by {@see speechSynthesisService}.
 * @param {object} resultToConfirm {@see ResponseObject} in {@see speechSynthesisService}.
 * @return {Promise} Resolves with the passed resultToConfirm if the user confirms. Rejects if we can't understand the user's
 *  confirmation or if the user confirms that the result that the {@see speechRecognitionService} understood is wrong.
 */
export const confirmResponseIfNecessary = (state, speechSynthesisFn, resultToConfirm) => {
    if (!state.confirmResponse) {
        return Promise.resolve(resultToConfirm)
    }
    incrementStats(state, 'confirmResponseCount')
    const speechSynthesisData = speechSynthesisFn(resultToConfirm.data)

    return state.getConfirmationFn({
        prompt: PeppermintServiceConstants.CONFIRMATION_PROMPT_FORMAT.replace('{0}', speechSynthesisData),
        retries: state.confirmationRetries,
        confirmResponse: false,
        waitForWakeWord: false,
    })
    .then((result) => {
        if (result.data === PeppermintServiceConstants.CONFIRMATION_RESULT_YES) {
            incrementStats(state, 'confirmResponseConfirmedCount')
            state.confirmedRecognition = resultToConfirm.data
            return resultToConfirm
        }

        state.confirmDeclinedRecognitions.push(resultToConfirm.data)

        return Promise.reject(new ConfirmationDeclinedException({
            message: 'Confirmation declined',
        }))
    })
}

/**
 * Rejects and emits an fault metric when necessary.
 * @param {object} err the error to reject with.
 * @param {Metric} fault metric to count.
 * @return {Promise} rejected promise with the error.
 */
export const handleError = (e, faultMetric) => {
    const inFaultWhitelist = NON_FAULT_EXCEPTION_WHITELIST.some((ExceptionConstructor) => {
        return e instanceof ExceptionConstructor
    })
    if (inFaultWhitelist) {
        faultMetric.count(0)
        return Promise.reject(e)
    }
    faultMetric.count(1)
    return Promise.reject(e)
}

/**
 * Initializes all dependencies and caches the promise.
 * @return {Promise} Resolves if initialization succeeds for all dependencies, rejects otherwise.
 */
export const initialize = () => {
    if (initializePromise) {
        return initializePromise
    }
    const message = {
        data: {
            action: 'warmup',
            parameters: '',
        },
    }
    initializePromise = VoiceUtilsService.verifyVoiceIsEnabled()
    .then(() => {
        return Promise.all([
            speechSynthesisService.warmup(),
            SpeechRecognitionService.warmup(),
            wakeWordListener(message),
        ])
    })
    .catch((e) => {
        initializePromise = null
        return Promise.reject(e)
    })
    return initializePromise
}

/**
 * Cancels a request that is waiting for the wake word (if any)
 */
export const cancelWakeWord = () => {
    const message = {
        data: {
            action: 'cancel',
            parameters: '',
        },
    }
    return wakeWordListener(message)
}

/**
 * Increments the statistics for the given statName.
 * @param {object} state {@see buildStateObject}.
 * @param {string} stateName The name of the stat to incremenent.
 */
export const incrementStats = (state, statName) => {
    const stats = state.stats
    if (!stats[statName]) {
        stats[statName] = 0
    }
    stats[statName]++
}

/**
 * Emits metrics for all stats generated and logs the comparison of correct vs incorrect recognitions.
 * @param {object} state {@see buildStateObject}.
 */
export const emitStatMetrics = (state) => {
    const stats = state.stats
    const statsMetricName = state.statsMetricName
    Object.keys(stats)
        .forEach((statName) => {
            const count = stats[statName]
            new CountMetric(undefined, statName, programName, 'peppermintService.' + statsMetricName).count(count)
        })

    // Report the misunderstandings. This will help us understand where the speech recognition is having trouble.
    if (state.confirmDeclinedRecognitions && state.confirmedRecognition) {
        console.debug('Wrong recognitions for', state.speechRecognitionFunctionName, 'actual value', state.confirmedRecognition, 'incorrect values', state.confirmDeclinedRecognitions)
    }
}

/**
 * Validates the config object and rejects if any of the required arguments are missing.
 * @param {object} config - The config object to validate.
 * @return {Promise} Resolves if the config is valid, rejcets with InvalidInputException if it isn't.
 */
export const validateConfig = (config) => {
    if (!config) {
        throw new InvalidInputException({
            message: 'config is required',
        })
    }
    if (!config.prompt || !_.isString(config.prompt)) {
        throw new InvalidInputException({
            message: 'config.prompt is required and must be a string',
        })
    }

    if (!_.isUndefined(config.retries)) {
        if (!_.isNumber(config.retries) || config.retries <= 0) {
            throw new InvalidInputException({
                message: 'config.retries must be a number greater than 0',
            })
        }
    }
    return Promise.resolve()
}
